import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { getError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Loader2, Download, Upload, Users, ListChecks, DatabaseBackup } from "lucide-react";

const PERMS = [
  { key: "add", label: "الإضافة" },
  { key: "edit", label: "التعديل" },
  { key: "delete", label: "الحذف" },
  { key: "query", label: "الاستعلام" },
];

const emptyUser = { name: "", username: "", password: "", department: "", role: "user", permissions: {} };

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(getError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyUser);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, username: u.username, password: "", department: u.department || "", role: u.role, permissions: u.permissions || {} });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, password: form.password || undefined };
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
        toast.success("تم تعديل الحساب بنجاح");
      } else {
        await api.post("/users", payload);
        toast.success("تمت إضافة الحساب بنجاح");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`هل أنت متأكد من حذف حساب ${u.name}؟`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("تم حذف الحساب");
      load();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-primary">إدارة حسابات المستخدمين</h2>
        <Button onClick={openAdd} className="bg-primary hover:bg-secondary transition-colors" data-testid="add-user-btn">
          <Plus className="h-4 w-4 ms-1" />
          إضافة حساب
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="bg-primary text-white">
              <th className="px-3 py-3 text-right font-bold">م</th>
              <th className="px-3 py-3 text-right font-bold">الاسم</th>
              <th className="px-3 py-3 text-right font-bold">اسم المستخدم</th>
              <th className="px-3 py-3 text-right font-bold">الإدارة</th>
              <th className="px-3 py-3 text-right font-bold">الدور</th>
              <th className="px-3 py-3 text-right font-bold">الصلاحيات</th>
              <th className="px-3 py-3 text-right font-bold">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></td></tr>
            ) : (
              users.map((u, i) => (
                <tr key={u.id} className="border-t border-border hover:bg-muted/50 transition-colors" data-testid={`user-row-${i}`}>
                  <td className="px-3 py-3">{i + 1}</td>
                  <td className="px-3 py-3 font-bold text-primary">{u.name}</td>
                  <td className="px-3 py-3">{u.username}</td>
                  <td className="px-3 py-3">{u.department}</td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${u.role === "admin" ? "bg-primary/10 text-primary" : "bg-secondary/15 text-secondary"}`}>
                      {u.role === "admin" ? "مسؤول" : "مستخدم"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">
                    {u.role === "admin" ? "كل الصلاحيات" : PERMS.filter((p) => u.permissions?.[p.key]).map((p) => p.label).join("، ") || "بدون صلاحيات"}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-secondary" data-testid={`edit-user-btn-${i}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(u)} className="text-red-600" data-testid={`delete-user-btn-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" dir="rtl" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-primary">{editing ? "تعديل حساب" : "إضافة حساب"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>الاسم</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label>اسم المستخدم</Label>
                <Input required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-username-input" />
              </div>
              <div className="space-y-2">
                <Label>كلمة المرور {editing && <span className="text-xs text-muted-foreground">(اتركها فارغة للإبقاء عليها)</span>}</Label>
                <Input type="password" required={!editing} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
              </div>
              <div className="space-y-2">
                <Label>الإدارة</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="user-department-input" />
              </div>
              <div className="space-y-2">
                <Label>الدور</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">مستخدم</SelectItem>
                    <SelectItem value="admin">مسؤول</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>الصلاحيات</Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={PERMS.every((p) => form.permissions[p.key])}
                    onCheckedChange={(v) => {
                      const all = Boolean(v);
                      setForm({ ...form, permissions: Object.fromEntries(PERMS.map((p) => [p.key, all])) });
                    }}
                    data-testid="perm-all-checkbox"
                  />
                  <span className="text-sm font-bold text-secondary">منح جميع الصلاحيات</span>
                </label>
              </div>
              <div className="flex flex-wrap gap-4 border border-border rounded-md p-3">
                {PERMS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={Boolean(form.permissions[p.key])}
                      onCheckedChange={(v) => setForm({ ...form, permissions: { ...form.permissions, [p.key]: Boolean(v) } })}
                      data-testid={`perm-${p.key}-checkbox`}
                    />
                    <span className="text-sm">{p.label}</span>
                  </label>
                ))}
              </div>
              {form.role === "admin" && (
                <p className="text-xs text-muted-foreground">المسؤول يملك جميع الصلاحيات تلقائياً</p>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-secondary transition-colors" data-testid="user-save-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin ms-2" />}
                {editing ? "حفظ التعديلات" : "إضافة الحساب"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ListManager({ title, endpoint, testid }) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/${endpoint}`);
      setItems(data);
    } catch {}
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post(`/${endpoint}`, { name: newName });
      setNewName("");
      toast.success("تمت الإضافة بنجاح");
      load();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/${endpoint}/${id}`, { name: editName });
      setEditId(null);
      toast.success("تم التعديل بنجاح");
      load();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${item.name}"؟`)) return;
    try {
      await api.delete(`/${endpoint}/${item.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <div className="bg-white rounded-lg border border-border shadow-[0_4px_20px_-4px_rgba(15,61,62,0.08)]" data-testid={testid}>
      <div className="bg-primary text-white rounded-t-lg px-5 py-3 font-bold">{title}</div>
      <form onSubmit={add} className="p-4 flex gap-2 border-b border-border">
        <Input placeholder="إضافة عنصر جديد..." value={newName} onChange={(e) => setNewName(e.target.value)} data-testid={`${testid}-add-input`} />
        <Button type="submit" disabled={busy} className="bg-secondary hover:bg-primary transition-colors shrink-0" data-testid={`${testid}-add-btn`}>
          <Plus className="h-4 w-4" />
        </Button>
      </form>
      <ul className="divide-y divide-border">
        {items.map((item, i) => (
          <li key={item.id} className="px-4 py-3 flex items-center justify-between gap-2" data-testid={`${testid}-item-${i}`}>
            {editId === item.id ? (
              <div className="flex gap-2 flex-1">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`${testid}-edit-input-${i}`} />
                <Button size="sm" onClick={() => saveEdit(item.id)} className="bg-secondary" data-testid={`${testid}-save-edit-btn-${i}`}>حفظ</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>إلغاء</Button>
              </div>
            ) : (
              <>
                <span className="font-medium text-primary">{item.name}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { setEditId(item.id); setEditName(item.name); }} className="text-secondary" data-testid={`${testid}-edit-btn-${i}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(item)} className="text-red-600" data-testid={`${testid}-delete-btn-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </li>
        ))}
        {items.length === 0 && <li className="px-4 py-6 text-center text-muted-foreground text-sm">لا توجد عناصر</li>}
      </ul>
    </div>
  );
}

function BackupTab() {
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const exportBackup = async () => {
    try {
      const { data } = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `نسخة-احتياطية-سجل-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("تم تصدير النسخة الاحتياطية");
    } catch (err) {
      toast.error(getError(err, "تعذر التصدير"));
    }
  };

  const importBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("سيتم استبدال جميع البيانات الحالية بمحتوى النسخة الاحتياطية. هل أنت متأكد؟")) return;
    setImporting(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      await api.post("/backup/import", payload);
      toast.success("تم استيراد النسخة الاحتياطية بنجاح");
    } catch (err) {
      toast.error(getError(err, "تعذر استيراد الملف، تأكد من صلاحيته"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="backup-tab">
      <div className="bg-white rounded-lg border border-border shadow-[0_4px_20px_-4px_rgba(15,61,62,0.08)] p-6 space-y-4">
        <div className="flex items-center gap-2 font-bold text-primary text-lg">
          <Download className="h-5 w-5 text-secondary" />
          تصدير الموقع
        </div>
        <p className="text-sm text-muted-foreground">تنزيل نسخة احتياطية كاملة تشمل القرارات والموظفين والمستخدمين والقوائم وبيانات الجهة بصيغة JSON.</p>
        <Button onClick={exportBackup} className="bg-primary hover:bg-secondary transition-colors" data-testid="backup-export-btn">
          <Download className="h-4 w-4 ms-1" />
          تصدير نسخة احتياطية
        </Button>
      </div>
      <div className="bg-white rounded-lg border border-border shadow-[0_4px_20px_-4px_rgba(15,61,62,0.08)] p-6 space-y-4">
        <div className="flex items-center gap-2 font-bold text-primary text-lg">
          <Upload className="h-5 w-5 text-secondary" />
          استيراد الموقع
        </div>
        <p className="text-sm text-muted-foreground">استعادة البيانات من ملف نسخة احتياطية سابق. تنبيه: سيتم استبدال البيانات الحالية بالكامل.</p>
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="border-secondary/40 text-secondary hover:bg-secondary hover:text-white transition-colors" data-testid="backup-import-btn">
          {importing ? <Loader2 className="h-4 w-4 animate-spin ms-1" /> : <Upload className="h-4 w-4 ms-1" />}
          استيراد نسخة احتياطية
        </Button>
        <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={importBackup} data-testid="backup-import-input" />
      </div>
    </div>
  );
}

export default function AdminPanel() {
  return (
    <div className="space-y-6" data-testid="admin-panel">
      <h1 className="text-2xl md:text-3xl font-extrabold text-primary">لوحة التحكم</h1>
      <Tabs defaultValue="users" dir="rtl">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="users" className="gap-2" data-testid="tab-users"><Users className="h-4 w-4" />حسابات المستخدمين</TabsTrigger>
          <TabsTrigger value="lists" className="gap-2" data-testid="tab-lists"><ListChecks className="h-4 w-4" />تعبئة القوائم</TabsTrigger>
          <TabsTrigger value="backup" className="gap-2" data-testid="tab-backup"><DatabaseBackup className="h-4 w-4" />النسخ الاحتياطي</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="lists" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ListManager title="قائمة الفئات" endpoint="categories" testid="manage-categories" />
            <ListManager title="قائمة الحالة" endpoint="statuses" testid="manage-statuses" />
          </div>
        </TabsContent>
        <TabsContent value="backup" className="mt-6"><BackupTab /></TabsContent>
      </Tabs>
    </div>
  );
}
